import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from "@heroui/react";
import { useEffect, useState } from "react";

import { errorMessage } from "@/lib/api";
import type { UserRole } from "@/lib/auth";
import { createUser, type UserCreatedResponse } from "./api";
import { ROLE_OPTIONS } from "./roles";

/**
 * ユーザーの新規登録ダイアログ。
 *
 * パスワードは入力しない。登録すると API が仮パスワードを生成して返すので、
 * 呼び出し側が [[TemporaryPasswordDialog]] で一度だけ見せる。管理者に
 * 決めさせないのは、弱いパスワードや全員同じパスワードの使い回しを防ぐため。
 */
export default function UserCreateDialog({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (created: UserCreatedResponse) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<UserRole>("view");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEmail("");
    setName("");
    setRole("view");
    setError(null);
    setSaving(false);
  }, [isOpen]);

  const submit = async () => {
    if (!email.trim()) {
      setError("メールアドレスを入力してください。");
      return;
    }

    setSaving(true);
    try {
      onCreated(
        await createUser({
          email: email.trim(),
          name: name.trim() || null,
          role,
        }),
      );
      onClose();
    } catch (e) {
      setError(errorMessage(e, "ユーザーを登録できませんでした。"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="lg">
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-1">
          <span>ユーザーの新規登録</span>
          <span className="text-small font-normal text-default-500">
            パスワードは入力しません。登録すると仮パスワードが発行されるので、本人に渡してください。
          </span>
        </ModalHeader>

        <ModalBody>
          <Input
            label="メールアドレス"
            labelPlacement="outside"
            placeholder=" "
            aria-label="メールアドレス"
            type="email"
            variant="bordered"
            radius="sm"
            isRequired
            value={email}
            onValueChange={(v) => {
              setEmail(v);
              setError(null);
            }}
          />
          <Input
            label="表示名"
            labelPlacement="outside"
            placeholder=" "
            aria-label="表示名"
            variant="bordered"
            radius="sm"
            value={name}
            onValueChange={setName}
          />
          <Select
            label="権限"
            labelPlacement="outside"
            aria-label="権限"
            variant="bordered"
            radius="sm"
            disallowEmptySelection
            selectedKeys={[role]}
            onSelectionChange={(keys) => {
              const key = [...keys][0] as UserRole | undefined;
              if (key) setRole(key);
            }}
          >
            {ROLE_OPTIONS.map((option) => (
              <SelectItem key={option.key} description={option.description}>
                {option.label}
              </SelectItem>
            ))}
          </Select>

          {error && <p className="text-sm text-danger">{error}</p>}
        </ModalBody>

        <ModalFooter>
          <Button variant="light" radius="full" data-testid="cancel" onPress={onClose}>
            キャンセル
          </Button>
          <Button
            color="primary"
            radius="full"
            data-testid="user-submit"
            isLoading={saving}
            onPress={() => void submit()}
          >
            登録
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
